/**
 * The table behind every chart (PRD 7.3: every chart has a table view, and the table is what
 * exports). Sorting, paging, and CSV are pure functions here; the component in
 * `public/js/components/data-table.js` is only the markup and the event wiring.
 *
 * Column shape:
 *   { key, label, type: 'text'|'number'|'date', align?, format?(value, row), csv?(value, row) }
 */

/** Compare two values of a column type, nulls last in both directions. */
export function compareValues(a, b, type) {
  const aMissing = a === null || a === undefined || a === '';
  const bMissing = b === null || b === undefined || b === '';
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  if (type === 'number') return Number(a) - Number(b);
  if (type === 'date') return String(a).localeCompare(String(b));
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * Sort rows by one column. Stable, and nulls stay last whichever way the column is sorted, so a
 * descending sort does not fill the first screen with blanks.
 * @param {object[]} rows
 * @param {{ key: string, type?: string }} column
 * @param {'asc'|'desc'} direction
 */
export function sortRows(rows, column, direction = 'asc') {
  if (!column) return [...rows];
  const sign = direction === 'desc' ? -1 : 1;
  return [...rows]
    .map((row, i) => ({ row, i }))
    .sort((x, y) => {
      const a = x.row[column.key];
      const b = y.row[column.key];
      const aMissing = a === null || a === undefined || a === '';
      const bMissing = b === null || b === undefined || b === '';
      if (aMissing !== bMissing) return aMissing ? 1 : -1;
      const cmp = compareValues(a, b, column.type) * sign;
      return cmp !== 0 ? cmp : x.i - y.i;
    })
    .map((x) => x.row);
}

/** One page of rows, with the numbers a pager needs. Page is 1-based and clamped. */
export function paginate(rows, { page = 1, pageSize = 25 } = {}) {
  const total = rows.length;
  const size = Math.max(1, pageSize);
  const pages = Math.max(1, Math.ceil(total / size));
  const current = Math.min(Math.max(1, Math.trunc(page) || 1), pages);
  const start = (current - 1) * size;
  return {
    rows: rows.slice(start, start + size),
    page: current,
    pages,
    pageSize: size,
    total,
    from: total === 0 ? 0 : start + 1,
    to: Math.min(start + size, total),
  };
}

/**
 * One CSV field. Quotes anything with a separator, quote, or newline, and prefixes a leading
 * `=`, `+`, `-`, or `@` with a quote so a spreadsheet reads it as text and not as a formula.
 */
export function csvField(value) {
  if (value === null || value === undefined) return '';
  let text = String(value);
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * CSV for the whole (unpaged) table: the column labels, then every row in the order the table
 * shows them. `csv` on a column wins over `format`, so a column can export the raw number while
 * the screen shows it rounded.
 * @param {object[]} rows
 * @param {Array<{ key: string, label: string, format?: Function, csv?: Function }>} columns
 */
export function toCsv(rows, columns) {
  const header = columns.map((c) => csvField(c.label)).join(',');
  const lines = rows.map((row) =>
    columns
      .map((c) => {
        const raw = row[c.key];
        const value = c.csv ? c.csv(raw, row) : raw;
        return csvField(value);
      })
      .join(','),
  );
  return [header, ...lines].join('\r\n');
}

/**
 * A column as a **specification** rather than a rendering (Phase 37). The screen and the export
 * have to agree about which columns a table has, what they are called, and what a cell's value
 * is; they disagree only about how a value is drawn. So the spec carries `key`, `label`, `type`,
 * a `unit` saying what kind of quantity it is, and — rarely — a `csv` projection for a value that
 * is not its own export (a list joined with a pipe, a boolean spelled out).
 *
 * The specs live in `lib/ui/`, beside the functions that build each view's export rows, so the
 * server can import both and produce a CSV that equals the table by construction rather than by
 * a second implementation that has to be kept in step. The component adds the drawing with
 * `withFormats`.
 */
export const UNITS = Object.freeze(['hours', 'percent', 'count', 'money', 'date', 'week', 'text']);

/**
 * Attach display formatters to a column spec. `byUnit` covers the common case (every hours
 * column draws the same way); `byKey` overrides one column (a status badge, a link).
 *
 * @param {Array<object>} columns  specs from a view's `*Columns` function
 * @param {{ byUnit?: Record<string, Function>, byKey?: Record<string, Function> }} formatters
 */
export function withFormats(columns, { byUnit = {}, byKey = {} } = {}) {
  return columns.map((c) => {
    const format = byKey[c.key] ?? byUnit[c.unit];
    return format ? { ...c, format } : c;
  });
}

/**
 * The column constructors every view's spec is written with. One place, so "an hours column" and
 * "a flag column" mean the same thing on every table and in every export.
 */
export const col = Object.freeze({
  text: (key, label, extra) => ({ key, label, type: 'text', unit: 'text', ...extra }),
  hours: (key, label, extra) => ({ key, label, type: 'number', unit: 'hours', ...extra }),
  count: (key, label, extra) => ({ key, label, type: 'number', unit: 'count', ...extra }),
  money: (key, label, extra) => ({ key, label, type: 'number', unit: 'money', ...extra }),
  percent: (key, label, extra) => ({ key, label, type: 'number', unit: 'percent', ...extra }),
  number: (key, label, extra) => ({ key, label, type: 'number', unit: 'number', ...extra }),
  week: (key, label, extra) => ({ key, label, type: 'date', unit: 'week', ...extra }),
  date: (key, label, extra) => ({ key, label, type: 'date', unit: 'date', ...extra }),
  /** A boolean: a tick on the screen, `yes` or `no` in the export, because a blank is not a no. */
  flag: (key, label, extra) => ({
    key,
    label,
    type: 'text',
    unit: 'flag',
    csv: (v) => (v ? 'yes' : 'no'),
    ...extra,
  }),
  /** A list: joined for reading on the screen, pipe-separated in the export. */
  list: (key, label, extra) => ({
    key,
    label,
    type: 'text',
    unit: 'list',
    csv: (v) => (Array.isArray(v) ? v.join('|') : (v ?? '')),
    ...extra,
  }),
});

/** A filename that sorts by date and says what it holds: `waag-heatmap-2026-09-15.csv`. */
export function csvFilename(name, date = new Date()) {
  const slug = String(name)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return `waag-${slug || 'table'}-${date.toISOString().slice(0, 10)}.csv`;
}
